import type { StorefrontOrder } from '@/lib/api'

const money = (n: number) => `$${n.toFixed(2)}`

/** Server timestamps are naive UTC; show them in store time, e.g. "Nov 3, 2026, 12:22 AM". */
export function formatOrderDate(placedAt: string): string {
  const utc = /[zZ]|[+-]\d\d:\d\d$/.test(placedAt) ? placedAt : `${placedAt}Z`
  return new Date(utc).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const STATUS_LABELS: Record<string, { label: string; tone: string }> = {
  pending: { label: 'Received', tone: 'bg-amber-100 text-amber-800' },
  ready: { label: 'Ready for pickup', tone: 'bg-emerald-100 text-emerald-800' },
  completed: { label: 'Picked up', tone: 'bg-stone-200 text-stone-700' },
  cancelled: { label: 'Cancelled', tone: 'bg-red-100 text-red-700' },
}

/** Shopper-facing label + badge colors for an order status. */
export function orderStatusBadge(status: string): { label: string; tone: string } {
  return STATUS_LABELS[status] ?? { label: status.charAt(0).toUpperCase() + status.slice(1), tone: 'bg-stone-100 text-stone-700' }
}

/**
 * Plain-text order summary encoded in the confirmation QR code (readable by any phone camera),
 * so staff can see the order at pickup.
 */
export function orderQrPayload(order: StorefrontOrder): string {
  const c = order.customer
  return [
    `Shree Sai Grocery order ${order.order_number}`,
    `Placed: ${formatOrderDate(order.placed_at)}`,
    `Customer: ${`${c.first_name} ${c.last_name}`.trim()} | ${c.phone ?? ''} | ${c.email}`,
    'Items:',
    ...order.lines.map((l) => `${l.quantity} x ${l.name}${l.item_num ? ` (#${l.item_num})` : ''} @ ${money(l.unit_price)}`),
    `Subtotal ${money(order.subtotal)} | Tax ${order.tax_rate_label} ${money(order.tax)} | Total ${money(order.total)}`,
    ...(order.notes.trim() ? [`Notes: ${order.notes.trim()}`] : []),
    'Pay at store',
  ].join('\n')
}
